import type {
  AppAttention,
  AttentionUrgency,
  BrowserOpenOptions,
  LogRecord,
  ResolvedNotification,
  SurfaceHostedIn,
} from '@hibro/shared';

/**
 * Control-plane contract between a page and the SharedWorker extension host
 * (见 18 Web 运行端).
 *
 * These envelopes ride the *same* `MessagePort` as the Channel RPC traffic and
 * are told apart by the `hibro:` prefix on `type` — RPC messages use the
 * `RpcMessage` shape (`kind: 'request' | 'response' | 'event'`) and never carry
 * a `type` field, so the two never collide.
 *
 * Kept in its own module (no DOM and no WebWorker globals) so the page project
 * and the worker project — which cannot share a `lib` — can both import it.
 */

/**
 * Where a detached surface actually ended up (见 18 / §5.8 通用 Detachable).
 *
 * A browser refuses `window.open` without transient user activation, and a
 * module-initiated detach (an incoming call) has no gesture behind it. Rather
 * than let that be the end of the story, the page falls back to hosting the very
 * same surface in a floating layer of its own — so the host still records the
 * surface and `attach`/`focus`/`list` keep working. The desktop has only the
 * `window` case, which is why this lives here and not in `@hibro/shared`.
 */
export type DetachPlacement = 'window' | 'inline';

/**
 * What a page needs to size and frame an in-page detached surface (见 18).
 *
 * Structured rather than the `features` string a real `window.open` takes: the
 * in-page host paints its own frame and would otherwise have to parse back out
 * what the driver just serialized.
 */
export interface DetachGeometry {
  width?: number;
  height?: number;
  /** Lower bounds while resizing, the in-page reading of `minWidth`/`minHeight`. */
  minWidth?: number;
  minHeight?: number;
  /** Whether the user may resize the frame. Default `true`. */
  resizable?: boolean;
  /** See `DetachFragmentOptions.chrome`: `false` = bare, view self-paints. */
  chrome?: boolean;
}

/** Worker → page. */
export type HostToPageMessage =
  /** A host/module observability record, forwarded to the page-side sink (见 15). */
  | { type: 'hibro:log'; record: LogRecord }
  /**
   * The durable flush asked for by `hibro:log-flush` has finished (the attempt,
   * not a guarantee the bytes landed — a failure is its own `hibro:log` record).
   */
  | { type: 'hibro:log-flushed'; requestId: number }
  /**
   * Sent once per connection, after the host attached its ChannelServer.
   *
   * `hostLockName` is this host instance's liveness beacon (见
   * {@link hostLivenessLockName}), which the page watches to learn that the
   * worker died. Optional for two reasons, both real: the host omits it when it
   * could not take the lock at all (no `navigator.locks`, or a rejected
   * request), so a page never watches a lock nobody holds; and this envelope is
   * posted before `hibro:hello` is read, so a page from another build receives
   * one from the outgoing host too — which predates the field.
   */
  | { type: 'hibro:ready'; hostLockName?: string }
  /**
   * The launch URL announced by this page has finished its one-time delivery.
   *
   * The page must not connect the workbench shell before this arrives: an OAuth
   * callback can change the account and gate state, and mounting the stale login
   * gate first would start a second redirect while that callback is still being
   * processed. The acknowledgement is per connection; reconnects announce the
   * origin root and receive the same barrier release without replaying a link.
   */
  | { type: 'hibro:startup-url-delivered' }
  /**
   * Live page-connection count, broadcast on every connect/disconnect. Exists so
   * the multi-tab guarantee is observable: two tabs on one worker report 2, and
   * closing one drops it back to 1 (proving the connection was really detached
   * rather than leaking, 见 18).
   */
  | { type: 'hibro:connections'; count: number }
  /**
   * A page from a different build connected, so this host is retiring (见 18
   * 版本换代). Every connected page reloads, which is what converges the whole
   * origin onto one generation; the host then closes its ports and calls
   * `self.close()` so the next construction of `/_ext-host.js` re-fetches.
   *
   * **Frozen contract**, like {@link PageToHostMessage} `hibro:hello.buildId`:
   * sender and receiver are by definition different builds of this app, so the
   * shape of this envelope can never change — only its meaning is allowed to be
   * "reload yourself".
   */
  | { type: 'hibro:generation-changed'; hostBuildId: string }
  /**
   * Open a URL in the page-owned Web browser host (见 22). The SharedWorker owns
   * authorization but has no DOM, so one selected page performs the operation
   * and settles it with `hibro:open-browser-result`.
   */
  | {
      type: 'hibro:open-browser';
      requestId: number;
      options: BrowserOpenOptions;
    }
  /**
   * Nobody claimed a link the user clicked, so the runtime gets to pick how to
   * show it (见 20 第三档 / 22). Distinct from `hibro:open-browser`, which is a
   * module asking for an in-app preview and therefore prescribes its own
   * presentation: this one leaves the choice to the page, which is the only end
   * that can see whether the click's transient activation is still alive and so
   * whether a real browser tab is on the table at all.
   *
   * Shares the request/result machinery (and id space) with the message above,
   * settling through the same `hibro:open-browser-result`.
   */
  | { type: 'hibro:open-unowned-link'; requestId: number; url: string }
  /**
   * A module asked to open a URL outside the app (`capabilities.openExternal`,
   * §5.6). The worker cannot open anything — no `window` — so the page performs
   * it, the browser counterpart of the desktop relaying to `shell.openExternal`
   * in the main process.
   */
  | { type: 'hibro:open-external'; url: string }
  /**
   * A `view:<viewId>[::<instanceKey>]` navigation targeting this page's
   * inline-dom views (见 8 / §5.8 跳转). Broadcast to every page, exactly as the
   * desktop main process fans `hibro:view:navigate` out to every window: each
   * page's `NavigationRegistry` routes it to the mounted view *instance* or
   * buffers it (§5.7). The gated iframe half is delivered by the host itself
   * (`navigateView`), not here.
   */
  | {
      type: 'hibro:navigate';
      viewId: string;
      instanceKey?: string;
      location: unknown;
    }
  /**
   * An iframe page renamed its own workbench tab (见 3 / 11). Broadcast to
   * every page, the same way `hibro:navigate` is: each page's workbench writes
   * the title onto that one dockview panel, and a page that does not hold the
   * panel leaves it.
   */
  | {
      type: 'hibro:set-view-title';
      viewId: string;
      instanceKey?: string;
      title: string;
    }
  /**
   * A notification the host's router sent to the OS (见 23). Sent to one page —
   * the worker has no `Notification` — mirroring how the desktop relays it to the
   * main process.
   *
   * The visibility decision is **no longer made here**: the host reports every
   * connection's `hibro:visibility` into its router, which chooses between this
   * and `hibro:notify-in-app`. So a hidden-tab notification still arrives as an
   * OS notification, and a visible one arrives as a toast instead of vanishing.
   */
  | { type: 'hibro:notify-os'; notification: ResolvedNotification }
  /**
   * The same notification, to be shown inside the app instead (见 23) — the user
   * is looking at a tab, so an OS notification would be redundant while dropping
   * it entirely would lose the message.
   *
   * Sent to **one** page, and specifically a visible one: a toast in three tabs
   * is the same message three times.
   */
  | { type: 'hibro:notify-in-app'; notification: ResolvedNotification }
  /**
   * Withdraw a notification by tag (需求 §4.4.3), because the owning module says
   * the user has now read it. Broadcast: the page holding the live `Notification`
   * is not necessarily the one still connected, and clearing a tag nobody has is
   * a no-op.
   */
  | { type: 'hibro:notify-dismiss'; tag: string }
  /**
   * The aggregated app-level attention state (见 23). **Broadcast**, unlike every
   * other relay here: this is a badge each page paints onto its own favicon and
   * title, so every tab needs it — the platform is not choosing one tab to
   * perform an action in. Also sent to a page as it connects, so a tab opened
   * after the unread arrived is not blank.
   */
  | { type: 'hibro:attention-state'; state: AppAttention }
  /**
   * Pull the user back to the app (见 23). Sent to one page, and only when the
   * pulse cleared the visibility gate — the same aggregation over every
   * connection's `hibro:visibility` the notification router reads, which is the
   * desktop's `isFocused()` check in this runtime's terms. A `critical` pulse
   * skips the gate.
   */
  | { type: 'hibro:attention-pulse'; urgency: AttentionUrgency }
  /**
   * End an in-flight pulse (见 23). Broadcast: any tab that is blinking must
   * stop, and a painter that is not pulsing treats this as a no-op.
   */
  | { type: 'hibro:attention-clear-pulse' }
  /**
   * A module asked to hand one of its own files to the user
   * (`capabilities.saveStorageFile`, 见 18). Sent to one page: only a document
   * can start a download. The worker resolves the module-relative path into a
   * storage URL first, so the page never learns which module or root is involved
   * — and no bytes cross this port, which is what makes this the route for a
   * large file. Unlike `hibro:open-external` this needs no user gesture: a
   * download is not a popup.
   *
   * `url` carries the download marker and filename in its query
   * (`STORAGE_DOWNLOAD_PARAM`), so the Service Worker answers it with
   * `Content-Disposition: attachment` — there is deliberately no separate
   * `suggestedName` field for the page to re-apply. The page's whole job is to
   * issue a request the SW will intercept.
   */
  | { type: 'hibro:save-file'; url: string }
  /**
   * Open a detached surface in its own browser window (§5.8 通用 Detachable).
   * Sent to one page because `window.open` needs a `window`; the reply comes
   * back as `hibro:detach-opened`. `name` is the stable window name derived from
   * the detach handle, so re-opening it focuses the existing window instead of
   * spawning a second one.
   *
   * `geometry` carries the same sizing as `features` in the shape the in-page
   * fallback needs, for when the browser refuses the popup (见
   * {@link DetachPlacement}).
   */
  | {
      type: 'hibro:detach-open';
      requestId: number;
      name: string;
      url: string;
      features: string;
      geometry: DetachGeometry;
    }
  /**
   * Last-resort focus via the workbench: re-open a detached window's `name`.
   * Real windows are focused with {@link HostToPageMessage} `hibro:detach-focus-self`
   * on their own connection instead — `window.open('', name)` from a new
   * workbench tab cannot see the opener's name and will spawn `about:blank`.
   */
  | { type: 'hibro:detach-focus'; name: string }
  /**
   * Tell a detached window to raise itself. Sent to that window's own
   * connection rather than to its opener: `window.focus()` still works after
   * the page that opened it is gone (same split as {@link HostToPageMessage}
   * `hibro:detach-close-self`).
   */
  | { type: 'hibro:detach-focus-self' }
  /**
   * Tell a detached window to close itself. Sent to that window's own
   * connection rather than to its opener: `window.close()` works on a
   * script-opened window, and this way closing still works after the page that
   * opened it is gone.
   */
  | { type: 'hibro:detach-close-self' }
  /**
   * Unmount a surface this page is hosting in-page (见 {@link DetachPlacement}).
   * The `inline` counterpart of `hibro:detach-close-self`, addressed to the
   * hosting page rather than to a window of its own.
   */
  | { type: 'hibro:detach-inline-close'; name: string }
  /**
   * Raise an in-page hosted surface to the top of the floating stack — the
   * `inline` counterpart of `hibro:detach-focus`. Unlike a real window this
   * always succeeds: no popup policy applies inside the page.
   */
  | { type: 'hibro:detach-inline-raise'; name: string }
  /**
   * Run a worker a module ships (`context.worker.spawn`, 见 18). Sent to one
   * page because this worker's global has neither `Worker` nor `SharedWorker` —
   * only a document can construct one. `url` is already resolved against the
   * module's own asset root by the host, so the page never learns where module
   * roots live and cannot be aimed at another module's code; `moduleId` rides
   * along for diagnostics. The reply is `hibro:worker-spawned`.
   */
  | {
      type: 'hibro:spawn-worker';
      /** Host-allocated; identifies both this request and the worker it yields. */
      workerId: number;
      moduleId: string;
      url: string;
      /**
       * Terminate every worker this page already hosts for `moduleId` before
       * constructing this one. A new host does not see the previous host's
       * registry, but the page does, and those workers can still hold an
       * exclusive OPFS sync access handle.
       */
      exclusive?: boolean;
    }
  /** Terminate a worker this page hosts (the module called `dispose`). */
  | { type: 'hibro:terminate-worker'; workerId: number };

/** Page → worker. */
export type PageToHostMessage =
  /**
   * First message on a new connection. `lockName` is a Web Lock the page holds
   * for its entire lifetime; the worker requests the same lock and only ever
   * acquires it once the page is gone, which is how a connection is *reliably*
   * detached — see {@link connectionLockName}.
   */
  | {
      type: 'hibro:hello';
      lockName: string;
      /**
       * The URL this page was loaded with (见 20). A worker cannot see it —
       * `self.location` is the *worker script's* URL, and `clients.matchAll()`
       * is Service Worker only — so the page forwards it and the host routes
       * any module deep link it carries (`<origin>/<moduleId>/<path>`). Almost
       * always an ordinary page URL, which the host ignores.
       */
      href: string;
      /** Initial value of {@link PageToHostMessage} `hibro:visibility`. */
      visible: boolean;
      /**
       * Which build this page came from (`import.meta.env.HIBRO_BUILD_ID`, 见 18
       * 版本换代). The host compares it with its own and retires on any
       * difference — no ordering, so the stamp needs no monotonicity.
       *
       * **Frozen contract, and optional forever**: the page sending this is not
       * necessarily the build that produced the host. Absent means "a page too
       * old to say", which is itself a mismatch — that page needs to reload as
       * much as a newer one does.
       */
      buildId?: string;
    }
  /**
   * Whether this page is currently in the foreground (`document.visibilityState`).
   * The host keeps the latest value per connection so it can answer "is the user
   * looking at the app right now?" — the desktop asks its single main window
   * (`isFocused()`), but a browser has N tabs and the worker can see none of
   * them. The aggregate is reported into the notification router (见 23) and read
   * by the attention pulse gate.
   */
  | { type: 'hibro:visibility'; visible: boolean }
  /** Result of a page-owned Web built-in browser open request. */
  | {
      type: 'hibro:open-browser-result';
      requestId: number;
      error?: string;
    }
  /**
   * Reverse navigation initiated by the page rather than by a module (见 8 /
   * §5.8 跳转) — currently an OS notification click. The counterpart of the
   * desktop's trusted-shell `hibro:navigate` IPC: the page is first-party, so
   * this is ungated and routed by the same host-side dispatcher a module's
   * `context.platform.navigate` goes through.
   */
  | { type: 'hibro:navigate-request'; target: string; location: unknown }
  /**
   * A delivered notification was clicked, on either surface (见 23). Carries only
   * the tag: the destination lives with the host's router, so an OS notification
   * click and an in-app toast click converge on one lookup instead of the page
   * routing each itself.
   */
  | { type: 'hibro:notification-click'; tag?: string }
  /**
   * The page is going away (`pagehide`). Browser ports do not error when their
   * page is gone, so without this the host would keep broadcasting into a dead
   * port. This is only the fast path for a graceful unload; the Web Lock above
   * is the guarantee (a closing tab may be torn down before this is delivered).
   */
  | { type: 'hibro:bye' }
  /**
   * OS dark-mode state (见 13 / §5.10). `matchMedia` does not exist in a worker,
   * so the page owns the media query and pushes flips here — the browser
   * counterpart of the main process's `nativeTheme` push.
   */
  | { type: 'hibro:os-theme'; osDark: boolean }
  /** OS locale (见 14 / §5.11), likewise read in the page via `navigator.language`. */
  | { type: 'hibro:os-locale'; locale: string }
  /**
   * The set of linked (load-unpacked) dev modules changed (见 11 调试 / 18): the
   * page linked, reloaded or unlinked one, and has finished copying it into
   * OPFS.
   *
   * Carries **no list**, deliberately. The copies live in the origin-private
   * filesystem, which every tab and the host all share, so that tree is the
   * single source of truth; a list assembled in one page could only ever be a
   * second, staler copy of it. The host re-reads and re-registers.
   */
  | { type: 'hibro:linked-modules-changed' }
  /**
   * Broker a gated connection for an `iframe` view (见 9 / 18). The page
   * transfers one end of a fresh `MessageChannel` with this message and hands
   * the other end to the iframe, so the sandboxed content talks to the host over
   * a connection scoped to `viewId` — and, when the view has several instances,
   * bound to `instanceKey` so navigation reaches this document only (§5.7). The
   * browser counterpart of the desktop main process brokering `connect-iframe`.
   */
  | {
      type: 'hibro:connect-iframe';
      viewId: string;
      instanceKey?: string;
      /** Which window the frame was mounted in (见 8). Omitted by a debug page. */
      hostedIn?: SurfaceHostedIn;
    }
  /**
   * Result of a `hibro:detach-open` this page was asked to perform (§5.8).
   *
   * `placement` says *how* the surface is being shown: `'window'` for a real
   * popup, `'inline'` when the browser refused one and this page took the
   * surface into its own floating layer instead (见 {@link DetachPlacement}).
   * `null` means neither was possible — the page has no workbench mounted yet —
   * and the host records nothing, so a refusal never leaves a detached entry
   * with nothing behind it.
   */
  | {
      type: 'hibro:detach-opened';
      requestId: number;
      placement: DetachPlacement | null;
    }
  /**
   * A surface this page hosted in-page is gone: the user closed its frame, or
   * the workbench that held it unmounted. The `inline` counterpart of a detached
   * window's connection dropping — that one is read from the Web Lock, but a
   * floating layer has no connection of its own, so it is reported explicitly.
   */
  | { type: 'hibro:detach-inline-closed'; name: string }
  /**
   * Result of a `hibro:spawn-worker` (见 18). On success the page transfers one
   * end of a fresh `MessageChannel` with this message and hands the other to the
   * worker, so the module's service and its worker talk **directly** and this
   * page drops out of the data path. `ok: false` means the worker could not be
   * constructed at all (a bad URL, a syntax error in the module's script).
   */
  | { type: 'hibro:worker-spawned'; workerId: number; ok: boolean }
  /**
   * A worker this page hosts has gone away on its own — it threw at load, or
   * `worker.onerror` fired. The other way a worker dies is its page being
   * closed, and that is *not* reported here: the page is gone, so the host reads
   * it from the connection's Web Lock instead (a port whose peer is gone never
   * errors, 见 {@link connectionLockName}).
   *
   * That split is precisely what lets the host tell a module **why** its worker
   * died (`WorkerCloseInfo`), so `detail` carries whatever the page observed —
   * the `error` event's message. Diagnostics only.
   */
  | { type: 'hibro:worker-closed'; workerId: number; detail?: string }
  /**
   * Renderer records to append to the daily log file (见 15). The page stamps
   * them; the host forces `source: 'renderer'` again and does **not** broadcast
   * them back — the sending page already has them in its ring, and echoing
   * would store one host line per connected tab.
   */
  | { type: 'hibro:log-append'; records: LogRecord[] }
  /**
   * Finish the current append batch before the page reads `hibro-logs` for a
   * download. Settles with `hibro:log-flushed`.
   */
  | { type: 'hibro:log-flush'; requestId: number };

/**
 * Name of the Web Lock a page holds for as long as it is alive.
 *
 * Why a lock rather than an unload message: a `MessagePort` whose page is gone
 * silently accepts `postMessage` (it never errors), and `pagehide` is not
 * guaranteed to be delivered before a closing tab is torn down — observed
 * failing exactly that way while bringing this up. A held Web Lock, by contrast,
 * is released by the browser itself when the page is destroyed, so the worker's
 * pending request for the same lock resolves precisely when the page dies.
 */
export function connectionLockName(connectionId: string): string {
  return `hibro:conn:${connectionId}`;
}

/**
 * Name of the Web Lock one host instance holds for as long as it is alive — the
 * mirror image of {@link connectionLockName}, with holder and observer swapped.
 *
 * The asymmetry it repairs: the host learns a page died from that page's lock,
 * but nothing told a page its host had died. `worker.onerror` covers only a
 * script that fails to load or evaluate; a SharedWorker the browser *reclaims*
 * (sleep, memory pressure, background discard) fires no event anywhere, and the
 * port to it keeps accepting `postMessage` forever — so every RPC after that
 * hangs unsettled with the workbench still painted. A lock is the one signal the
 * browser itself is obliged to give: it releases this one when the worker is
 * terminated, whatever the reason.
 *
 * Named per host *instance* rather than per origin, which is what keeps it a
 * beacon instead of a mutex: {@link EXT_HOST_LOCK_NAME} is contended by design
 * and may be waived on a timeout, so a page watching *that* name would read
 * "nobody holds it yet" as "the host is gone". Nothing ever contends this one.
 */
export function hostLivenessLockName(hostId: string): string {
  return `hibro:host:${hostId}`;
}

/**
 * Web Lock naming the right to *be* the extension host for this origin (见 18
 * 版本换代). One holder at a time, held for the host's whole life.
 *
 * The frozen URL of `/_ext-host.js` already keeps a single scope per origin, so
 * this is deliberately redundant — its value is that it does not depend on that
 * invariant. Whenever two hosts *can* exist (the deploy that introduces this
 * mechanism, or a future change to the worker URL), the second one finds the lock
 * taken and stays out of IndexedDB and OPFS instead of racing the first.
 *
 * **Frozen contract** for the same reason as the envelopes above: the two hosts
 * contending for it are different builds, so the name can never change.
 */
export const EXT_HOST_LOCK_NAME = 'hibro:ext-host';

/** Whether a port message is a control-plane envelope rather than RPC traffic. */
export function isControlMessage(data: unknown): boolean {
  return (
    typeof (data as { type?: unknown } | null)?.type === 'string' &&
    (data as { type: string }).type.startsWith('hibro:')
  );
}
